/**
 * Zustand store for About Us data
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const ABOUT_KEY = "khays_about";

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  imageUrl: string;
  order: number;
  group: string;
}

export interface AboutData {
  storeDescription: string;
  members: TeamMember[];
  groups: string[];
}

function loadFromStorage(): AboutData {
  try { const s = localStorage.getItem(ABOUT_KEY); if (s) return JSON.parse(s); } catch {}
  return { storeDescription: "", members: [], groups: [] };
}

interface AboutState {
  data: AboutData;
  _rev: number;
  setData: (data: AboutData) => void;
  reload: () => void;
}

export const useAboutStore = create<AboutState>()(
  devtools(
    (set, get) => ({
      data: loadFromStorage(),
      _rev: 0,
      setData: (data) => {
        localStorage.setItem(ABOUT_KEY, JSON.stringify(data));
        set({ data, _rev: get()._rev + 1 });
      },
      reload: () => set({ data: loadFromStorage(), _rev: get()._rev + 1 }),
    }),
    { name: "AboutStore" }
  )
);

function save(data: AboutData): void { useAboutStore.getState().setData(data); }
function load(): AboutData { return useAboutStore.getState().data; }

export function getAboutData(): AboutData {
  const data = load();
  return { ...data, members: [...data.members].sort((a, b) => a.order - b.order) };
}

export function updateStoreDescription(description: string): void {
  save({ ...load(), storeDescription: description });
}

export function addTeamMember(name: string, role: string, bio = "", imageUrl = "", group = ""): TeamMember {
  const data = load();
  const member: TeamMember = {
    id: `member_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    name, role, bio, imageUrl, order: data.members.length, group: group || "",
  };
  save({ ...data, members: [...data.members, member] });
  return member;
}

export function reorderTeamMember(id: string, direction: "up" | "down"): void {
  const data = load();
  const members = [...data.members];
  const idx = members.findIndex(m => m.id === id);
  if (idx < 0) return;
  const swapIdx = direction === "up" ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= members.length) return;
  [members[idx], members[swapIdx]] = [members[swapIdx], members[idx]];
  members.forEach((m, i) => { m.order = i; });
  save({ ...data, members });
}

export function updateTeamMemberGroup(id: string, group: string): void {
  const data = load();
  save({ ...data, members: data.members.map(m => m.id === id ? { ...m, group } : m) });
}

export function addTeamGroup(name: string): void {
  const data = load();
  if (!(data.groups || []).includes(name)) {
    save({ ...data, groups: [...(data.groups || []), name] });
  }
}

export function removeTeamGroup(name: string): void {
  const data = load();
  save({
    ...data,
    groups: (data.groups || []).filter(g => g !== name),
    members: data.members.map(m => m.group === name ? { ...m, group: "" } : m),
  });
}

export function renameTeamGroup(oldName: string, newName: string): void {
  if (!newName.trim() || oldName === newName) return;
  const data = load();
  save({
    ...data,
    groups: data.groups.map(g => g === oldName ? newName.trim() : g),
    members: data.members.map(m => m.group === oldName ? { ...m, group: newName.trim() } : m),
  });
}

export function updateTeamMember(id: string, updates: Partial<Pick<TeamMember, "name" | "role" | "bio" | "imageUrl">>): void {
  const data = load();
  save({ ...data, members: data.members.map(m => m.id === id ? { ...m, ...updates } : m) });
}

export function removeTeamMember(id: string): void {
  const data = load();
  const members = data.members.filter(m => m.id !== id).map((m, i) => ({ ...m, order: i }));
  save({ ...data, members });
}
